/* eslint-disable */
import React, { useState, useMemo, useRef } from 'react';
import { useAppData } from '../../context/AppDataContext';
import { prospectsAPI } from '../../services/api';
import { formatMoney, symbolFor } from '../../utils/currencyTotals';
import { validateCurrency } from '../../utils/currencies';

const STATUSES = [
  { value: 'analysis', label: '🔍 Analysis', color: '#3b82f6' },
  { value: 'offer-made', label: '📝 Offer Made', color: '#f59e0b' },
  { value: 'offer-accepted', label: '✅ Offer Accepted', color: '#22c55e' },
  { value: 'legals', label: '⚖️ Legals', color: '#8b5cf6' },
  { value: 'purchased', label: '🏠 Purchased', color: '#15803d' },
  { value: 'lost', label: '❌ Lost', color: '#ef4444' }
];

const REFURB_CATS = ['Architect Fees','Bathroom','Carpets and Flooring','Clean Out','Electrical and Plumbing','Furniture','Kitchen','Loft Conversion','Painting and Decorating','Planning Fees','Plastering','Roof','Windows and Doors'];

const defaultForm = {
  status: 'analysis', address: '', askingPrice: '', salePrice: '', bedrooms: '',
  refurbCosts: 0, purchaseCosts: 0, finishedValue: '', expectedRent: '',
  expenseManagement: 0, expenseUtilities: 0, expenseMortgage: 0, expenseRepairs: 0,
  mortgageRate: 0, propertyLink: '', notes: '', refurbBreakdown: {},
  currency: 'GBP'   // BUG-005e: GBP or EUR; every figure on the page uses this prospect's own symbol
};

// ─── ROI Calculator ───────────────────────────────────────────────────────────
function calcROI(p) {
  const ask = parseFloat(p.askingPrice||p.asking_price) || 0;
  const sale = parseFloat(p.salePrice||p.sale_price) || 0;
  const refurb = parseFloat(p.refurbCosts||p.refurb_costs) || 0;
  const pCosts = parseFloat(p.purchaseCosts||p.purchase_costs) || 0;
  const rent = parseFloat(p.expectedRent||p.expected_rent) || 0;
  const fv = parseFloat(p.finishedValue||p.finished_value) || 0;
  const mgmt = parseFloat(p.expenseManagement) || 0;
  const utils = parseFloat(p.expenseUtilities) || 0;
  const mort = parseFloat(p.expenseMortgage) || 0;
  const repairs = parseFloat(p.expenseRepairs) || 0;
  const actual = sale > 0 ? sale : ask;
  const totalInv = actual + refurb + pCosts;
  const monthlyExp = mgmt + utils + mort + repairs;
  const annRent = rent * 12, annExp = monthlyExp * 12, annNet = annRent - annExp;
  const grossYield = totalInv > 0 ? (annRent / totalInv) * 100 : 0;
  const netYield = totalInv > 0 ? (annNet / totalInv) * 100 : 0;
  const roi = totalInv > 0 ? ((fv - totalInv + annNet) / totalInv) * 100 : 0;
  const capitalGain = fv - totalInv;
  const cashFlow = rent - monthlyExp;
  const rate = (parseFloat(p.mortgageRate||p.mortgage_rate) || 0) / 100;
  const scenarios = [75,70,65,60].map(ltv => {
    const mortAmt = fv*(ltv/100), cashOut = mortAmt-totalInv, leftIn = totalInv-mortAmt;
    return { ltv, mortAmt, cashOut, leftIn, roce: leftIn>0?(annNet/leftIn)*100:0, monthlyMort: rate>0?(mortAmt*rate)/12:0 };
  });
  return { actual, totalInv, annRent, monthlyExp, annNet, grossYield, netYield, roi, capitalGain, cashFlow, scenarios };
}

// ─── Projections Calculator ───────────────────────────────────────────────────
function calcProjections(p, rentalGrowth, valueGrowth, years) {
  const roi = calcROI(p);
  const initRent = parseFloat(p.expectedRent||p.expected_rent) || 0;
  const initValue = parseFloat(p.finishedValue||p.finished_value) || 0;
  const monthlyExp = roi.monthlyExp;
  const projections = [];
  let cumIncome = 0, cumExp = 0;
  for (let yr = 1; yr <= years; yr++) {
    const yearlyRent = initRent * Math.pow(1 + rentalGrowth/100, yr-1) * 12;
    const yearlyExp = monthlyExp * 12;
    const yearlyNet = yearlyRent - yearlyExp;
    const propValue = initValue * Math.pow(1 + valueGrowth/100, yr);
    const equity = propValue - roi.totalInv;
    cumIncome += yearlyRent; cumExp += yearlyExp;
    const cumNet = cumIncome - cumExp;
    const totalReturn = equity + cumNet;
    const totalROI = roi.totalInv > 0 ? (totalReturn / roi.totalInv)*100 : 0;
    projections.push({ year:yr, propValue, monthlyRent: initRent*Math.pow(1+rentalGrowth/100,yr-1), yearlyRent, yearlyNet, equity, cumNet, totalReturn, totalROI });
  }
  return projections;
}

// ─── Comparison Score ─────────────────────────────────────────────────────────
function calcScore(roi, cashFlow) {
  let score = 0;
  const ltv75 = roi.scenarios[0];
  if (cashFlow > 500) score += 30; else if (cashFlow > 300) score += 25; else if (cashFlow > 100) score += 15; else score += 5;
  const leftIn = ltv75?.leftIn || 0;
  if (leftIn < 10000) score += 25; else if (leftIn < 20000) score += 20; else if (leftIn < 30000) score += 10; else score += 5;
  const roce = ltv75?.roce || 0;
  if (roce > 25) score += 20; else if (roce > 15) score += 15; else if (roce > 10) score += 10; else score += 5;
  if (roi.grossYield > 7) score += 15; else if (roi.grossYield > 5) score += 12; else if (roi.grossYield > 4) score += 8; else score += 3;
  if (roi.roi > 20) score += 10; else if (roi.roi > 15) score += 8; else if (roi.roi > 10) score += 5; else score += 2;
  return score;
}

// ─── Helper to normalise field names from server ──────────────────────────────
function normalise(p) {
  if (!p) return p;
  const rb = (typeof p.refurb_breakdown === 'string' ? JSON.parse(p.refurb_breakdown||'{}') : p.refurb_breakdown) || {};
  return {
    ...p,
    askingPrice: p.asking_price || p.askingPrice || 0,
    salePrice: p.sale_price || p.salePrice || 0,
    expectedRent: p.expected_rent || p.expectedRent || 0,
    finishedValue: p.finished_value || p.finishedValue || 0,
    refurbCosts: p.refurb_costs || p.refurbCosts || 0,
    purchaseCosts: p.purchase_costs || p.purchaseCosts || 0,
    mortgageRate: p.mortgage_rate || p.mortgageRate || 0,
    expenseManagement: rb._mgmt !== undefined ? rb._mgmt : (parseFloat(p.expenseManagement) || parseFloat(p.expense_management) || 0),
    expenseUtilities: rb._utils !== undefined ? rb._utils : (parseFloat(p.expenseUtilities) || parseFloat(p.expense_utilities) || 0),
    expenseMortgage: rb._mort !== undefined ? rb._mort : (parseFloat(p.expenseMortgage) || parseFloat(p.expense_mortgage) || 0),
    expenseRepairs: rb._rep !== undefined ? rb._rep : (parseFloat(p.expenseRepairs) || parseFloat(p.expense_repairs) || 0),
    propertyLink: rb._link !== undefined ? rb._link : (p.propertyLink || p.property_link || ''),
    bedrooms: rb._beds !== undefined ? rb._beds : (p.bedrooms !== undefined && p.bedrooms !== null ? p.bedrooms : 0),
    documents: rb._docs || p.documents || [],
    refurbBreakdown: rb,
    currency: validateCurrency(p.currency).value || 'GBP'
  };
}

export default function Prospects() {
  const { prospects, setProspects } = useAppData();
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editProspect, setEditProspect] = useState(null);
  const [showRefurb, setShowRefurb] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analysis');
  const [rentalGrowth, setRentalGrowth] = useState(3);
  const [valueGrowth, setValueGrowth] = useState(3);
  const [form, setForm] = useState(defaultForm);
  const [refurbBreakdown, setRefurbBreakdown] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const fileInputRef = useRef(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const nProspects = useMemo(() => prospects.map(normalise), [prospects]);
  const active = nProspects.filter(p => !p.archived);
  const archived = nProspects.filter(p => p.archived);

  const refurbTotal = useMemo(() => {
    let t = Object.values(refurbBreakdown).reduce((s,v) => s+(parseFloat(v)||0), 0);
    customItems.forEach(i => t += parseFloat(i.cost)||0);
    return t;
  }, [refurbBreakdown, customItems]);

  const totalMonthlyExp = (parseFloat(form.expenseManagement)||0)+(parseFloat(form.expenseUtilities)||0)+(parseFloat(form.expenseMortgage)||0)+(parseFloat(form.expenseRepairs)||0);

  const openAdd = () => {
    setForm(defaultForm); setRefurbBreakdown({}); setCustomItems([]);
    setEditProspect(null); setShowModal(true); setActiveTab('analysis');
  };

  // Shared helper: loads a prospect into form + refurb state
  const loadProspectIntoForm = (p) => {
    const n = normalise(p);
    setForm({
      status: n.status||'analysis', address: n.address||'', askingPrice: n.askingPrice||'',
      salePrice: n.salePrice||'', bedrooms: (n.bedrooms !== undefined && n.bedrooms !== null && !isNaN(n.bedrooms)) ? String(n.bedrooms) : '',
      refurbCosts: n.refurbCosts||0, purchaseCosts: n.purchaseCosts||0,
      finishedValue: n.finishedValue||'', expectedRent: n.expectedRent||'',
      expenseManagement: n.expenseManagement||0, expenseUtilities: n.expenseUtilities||0,
      expenseMortgage: n.expenseMortgage||0, expenseRepairs: n.expenseRepairs||0,
      mortgageRate: n.mortgageRate||0, propertyLink: n.propertyLink||'',
      notes: n.notes||'', refurbBreakdown: n.refurbBreakdown||{},
      currency: n.currency
    });
    const rb = n.refurbBreakdown || {};
    const cats = {}; const custom = [];
    REFURB_CATS.forEach(c => { if (rb[c]) cats[c] = rb[c]; });
    if (rb.Contingency) cats['Contingency'] = rb.Contingency;
    Object.keys(rb).forEach(k => {
      if (!REFURB_CATS.includes(k) && k !== 'Contingency' && !k.startsWith('_'))
        custom.push({ name: k, cost: rb[k] });
    });
    setRefurbBreakdown(cats);
    setCustomItems(custom);
    setEditProspect(p);
  };

  const openEdit = (p) => {
    loadProspectIntoForm(p);
    setShowModal(true);
  };

  // Opens the refurb calculator directly WITHOUT the property edit modal
  const openRefurbDirect = (p) => {
    loadProspectIntoForm(p);
    setShowModal(false);
    setShowRefurb(true);
  };

  // Build the refurbBreakdown object that goes to the server, embedding all extra fields
  const buildPayload = (extraDocs, bedroomOverride) => {
    const breakdown = { ...refurbBreakdown };
    customItems.forEach(i => { if (i.name) breakdown[i.name] = Math.round(parseFloat(i.cost)||0); });
    // Round all standard category values to whole pounds to prevent float drift
    Object.keys(breakdown).forEach(k => {
      if (!k.startsWith('_')) breakdown[k] = Math.round(parseFloat(breakdown[k])||0);
    });
    // Embed individual expense fields and other extra data into refurb_breakdown
    breakdown._mgmt = parseFloat(form.expenseManagement)||0;
    breakdown._utils = parseFloat(form.expenseUtilities)||0;
    breakdown._mort = parseFloat(form.expenseMortgage)||0;
    breakdown._rep = parseFloat(form.expenseRepairs)||0;
    breakdown._link = form.propertyLink||'';
    // Use explicit override if provided (avoids stale form.bedrooms in async closures)
    if (bedroomOverride !== undefined && bedroomOverride !== null) {
      breakdown._beds = parseInt(bedroomOverride, 10) || 0;
    } else {
      const bedsVal = parseInt(form.bedrooms, 10); breakdown._beds = (!isNaN(bedsVal) && form.bedrooms !== '') ? bedsVal : 0;
    }
    if (extraDocs) breakdown._docs = extraDocs;
    return breakdown;
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const existingDocs = editProspect ? (normalise(editProspect).documents||[]) : [];
      const breakdown = buildPayload(existingDocs);
      const data = {
        status: form.status, address: form.address,
        askingPrice: Math.round(parseFloat(form.askingPrice)||0), salePrice: Math.round(parseFloat(form.salePrice)||0),
        bedrooms: form.bedrooms !== '' ? parseInt(form.bedrooms, 10) : 0,
        refurbCosts: Math.round(refurbTotal),
        purchaseCosts: Math.round(parseFloat(form.purchaseCosts)||0),
        finishedValue: Math.round(parseFloat(form.finishedValue)||0),
        expectedRent: Math.round(parseFloat(form.expectedRent)||0),
        monthlyExpenses: totalMonthlyExp,
        mortgageRate: parseFloat(form.mortgageRate)||0,
        notes: form.notes, propertyLink: form.propertyLink, currency: form.currency,
        refurbBreakdown: breakdown,
        expenseManagement: parseFloat(form.expenseManagement)||0,
        expenseUtilities: parseFloat(form.expenseUtilities)||0,
        expenseMortgage: parseFloat(form.expenseMortgage)||0,
        expenseRepairs: parseFloat(form.expenseRepairs)||0,
        archived: false
      };
      if (editProspect) {
        const r = await prospectsAPI.update(editProspect.id, data);
        const updated = normalise(r);
        // Update both the list and selected with the fresh server response
        setProspects(prev => prev.map(p => p.id === updated.id ? r : p));
        if (selected?.id === updated.id) setSelected(r);
      } else {
        const r = await prospectsAPI.create(data);
        const created = normalise(r);
        setProspects(prev => [created, ...prev]);
        setSelected(created); setActiveTab('analysis');
      }
      setShowModal(false);
      setEditProspect(null);
    } catch (err) { alert(err.error||'Failed to save prospect'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prospect?')) return;
    try { await prospectsAPI.delete(id); setProspects(prev=>prev.filter(p=>p.id!==id)); if(selected?.id===id) setSelected(null); }
    catch (err) { alert(err.error||'Failed'); }
  };

  const handleArchive = async (p) => {
    try {
      const n = normalise(p);
      const r = await prospectsAPI.update(p.id, { ...n, askingPrice:n.askingPrice, salePrice:n.salePrice, expectedRent:n.expectedRent, finishedValue:n.finishedValue, refurbCosts:n.refurbCosts, purchaseCosts:n.purchaseCosts, monthlyExpenses:n.expenseManagement+n.expenseUtilities+n.expenseMortgage+n.expenseRepairs, mortgageRate:n.mortgageRate, refurbBreakdown:n.refurbBreakdown, archived: !p.archived });
      const updated = normalise(r);
      setProspects(prev => prev.map(x => x.id===p.id ? updated : x));
      if (selected?.id === p.id) setSelected(null);
    } catch (err) { alert(err.error||'Failed'); }
  };

  // ─── Document upload ────────────────────────────────────────────────────────
  const handleFileUpload = async (e, prospect) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const n = normalise(prospect);
    const existingDocs = n.documents || [];
    const newDocs = await Promise.all(files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = ev => resolve({ name: file.name, type: file.type, size: file.size, data: ev.target.result, uploadedAt: new Date().toISOString() });
      reader.readAsDataURL(file);
    })));
    const allDocs = [...existingDocs, ...newDocs];
    const rb = { ...n.refurbBreakdown, _docs: allDocs };
    try {
      const r = await prospectsAPI.update(prospect.id, { ...n, askingPrice:n.askingPrice, salePrice:n.salePrice, expectedRent:n.expectedRent, finishedValue:n.finishedValue, refurbCosts:n.refurbCosts, purchaseCosts:n.purchaseCosts, monthlyExpenses:totalMonthlyExp||n.expenseManagement+n.expenseUtilities+n.expenseMortgage+n.expenseRepairs, mortgageRate:n.mortgageRate, refurbBreakdown:rb });
      const updated = normalise(r);
      setProspects(prev => prev.map(x => x.id===prospect.id ? updated : x));
      setSelected(updated);
    } catch (err) { alert(err.error||'Failed to save document'); }
  };

  const handleDeleteDoc = async (prospect, idx) => {
    if (!window.confirm('Delete this document?')) return;
    const n = normalise(prospect);
    const docs = (n.documents||[]).filter((_,i) => i!==idx);
    const rb = { ...n.refurbBreakdown, _docs: docs };
    try {
      const r = await prospectsAPI.update(prospect.id, { ...n, askingPrice:n.askingPrice, salePrice:n.salePrice, expectedRent:n.expectedRent, finishedValue:n.finishedValue, refurbCosts:n.refurbCosts, purchaseCosts:n.purchaseCosts, monthlyExpenses:n.expenseManagement+n.expenseUtilities+n.expenseMortgage+n.expenseRepairs, mortgageRate:n.mortgageRate, refurbBreakdown:rb });
      const updated = normalise(r);
      setProspects(prev => prev.map(x => x.id===prospect.id ? updated : x));
      setSelected(updated);
    } catch (err) { alert(err.error||'Failed'); }
  };

  // ─── Compare toggle ──────────────────────────────────────────────────────────
  const toggleCompare = (id) => {
    setCompareIds(prev => {
      if (prev.includes(id)) return prev.filter(x=>x!==id);
      if (prev.length >= 2) { alert('Select exactly 2 to compare. Uncheck one first.'); return prev; }
      return [...prev, id];
    });
  };

  // ─── DETAIL VIEW ─────────────────────────────────────────────────────────────
  if (selected) {
    const p = nProspects.find(x=>x.id===selected.id);
    // If selected prospect no longer exists (e.g. was deleted), clear and return to list
    if (!p) { setTimeout(() => setSelected(null), 0); return null; }
    const roi = calcROI(p);
    const status = STATUSES.find(s=>s.value===p.status)||STATUSES[0];
    const link = p.propertyLink;
    const docs = p.documents||[];
    const MC = ({label,value,color,sub}) => (
      <div className="stat-card">
        <div className="stat-label">{label}</div>
        <div className="stat-value" style={{color:color||'inherit'}}>{value}</div>
        {sub&&<div style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>{sub}</div>}
      </div>
    );
    const proj5 = calcProjections(p, rentalGrowth, valueGrowth, 5);
    const proj10 = calcProjections(p, rentalGrowth, valueGrowth, 10);
    const proj20 = calcProjections(p, rentalGrowth, valueGrowth, 20);
    const rb = p.refurbBreakdown || {};
    const refurbEntries = Object.entries(rb).filter(([k,v]) => !k.startsWith('_') && parseFloat(v)>0);

    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
          <button className="btn btn-secondary" onClick={()=>setSelected(null)}>← Back</button>
          <div style={{display:'flex',gap:'0.75rem'}}>
            <button className="btn btn-secondary" onClick={()=>openEdit(p)}>✏️ Edit</button>
            <button className="btn btn-secondary" style={{background:'#f59e0b',color:'white',border:'none'}} onClick={()=>handleArchive(p)}>{p.archived?'📤 Unarchive':'📦 Archive'}</button>
            <button className="btn btn-danger" onClick={()=>handleDelete(p.id)}>🗑️</button>
          </div>
        </div>

        <div className="card" style={{marginBottom:'1.5rem',background:'linear-gradient(135deg,var(--brand-green) 0%,var(--brand-green-light) 100%)',color:'white'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div>
              <h2 style={{fontSize:'1.5rem',fontWeight:700,marginBottom:'0.5rem'}}>{p.address}</h2>
              <div style={{display:'flex',gap:'1rem',flexWrap:'wrap',fontSize:'0.875rem',opacity:0.9}}>
                <span style={{background:'rgba(255,255,255,0.2)',padding:'0.2rem 0.75rem',borderRadius:'1rem'}}>{status.label}</span>
                {p.bedrooms>0&&<span>🛏️ {p.bedrooms} bed{p.bedrooms!==1?'s':''}</span>}
                <span>💰 {formatMoney(p.askingPrice, p.currency)}</span>
                {p.salePrice>0&&<span>✅ Sale: {formatMoney(p.salePrice, p.currency)}</span>}
              </div>
            </div>
            {link&&<a href={link} target="_blank" rel="noreferrer" style={{background:'rgba(255,255,255,0.2)',color:'white',padding:'0.5rem 1rem',borderRadius:'0.5rem',textDecoration:'none',fontWeight:600}}>🔗 View Listing</a>}
          </div>
        </div>

        <div className="tabs">
          {[['analysis','📊 Analysis'],['details','📋 Details'],['refurb','🔧 Refurb'],['refinance','🏦 Refinance'],['projections','📈 Projections'],['documents','📎 Documents']].map(([val,label])=>(
            <button key={val} className={`tab-btn ${activeTab===val?'active':''}`} onClick={()=>setActiveTab(val)}>{label}</button>
          ))}
        </div>

        {/* ANALYSIS */}
        {activeTab==='analysis'&&(
          <div>
            <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
              <MC label="Total Investment" value={formatMoney(roi.totalInv, p.currency)}/>
              <MC label="Gross Yield" value={`${roi.grossYield.toFixed(2)}%`} color={roi.grossYield>=8?'#15803d':roi.grossYield>=6?'#d97706':'#dc2626'}/>
              <MC label="Net Yield" value={`${roi.netYield.toFixed(2)}%`} color={roi.netYield>=6?'#15803d':roi.netYield>=4?'#d97706':'#dc2626'}/>
              <MC label="Monthly Cash Flow" value={formatMoney(roi.cashFlow, p.currency)} color={roi.cashFlow>=0?'#15803d':'#dc2626'}/>
              <MC label="Annual Net Income" value={formatMoney(roi.annNet, p.currency)} color={roi.annNet>=0?'#15803d':'#dc2626'}/>
              <MC label="Capital Gain" value={formatMoney(roi.capitalGain, p.currency)} color={roi.capitalGain>=0?'#15803d':'#dc2626'} sub={roi.totalInv>0?`${((roi.capitalGain/roi.totalInv)*100).toFixed(1)}%`:''}/>
              <MC label="ROI" value={`${roi.roi.toFixed(1)}%`} color={roi.roi>=15?'#15803d':roi.roi>=10?'#d97706':'#dc2626'}/>
              <MC label="ROCE" value={`${roi.totalInv>0?((roi.annNet/roi.totalInv)*100).toFixed(1):0}%`}/>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">💸 Investment Breakdown</span></div>
              {[['Purchase Price',formatMoney(roi.actual, p.currency)],['Refurbishment',formatMoney(p.refurbCosts, p.currency)],['Purchase Costs',formatMoney(p.purchaseCosts, p.currency)],['Total Investment',formatMoney(roi.totalInv, p.currency)],['Finished Value',formatMoney(p.finishedValue, p.currency)],['Gross Profit',formatMoney(roi.capitalGain, p.currency)]].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border-light)'}}><span style={{color:'var(--text-secondary)'}}>{l}</span><strong>{v}</strong></div>
              ))}
            </div>
          </div>
        )}

        {/* DETAILS */}
        {activeTab==='details'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.5rem'}}>
            <div className="card">
              <div className="card-header"><span className="card-title">📋 Property Info</span></div>
              {[['Address',p.address],['Status',status.label],['Bedrooms',p.bedrooms||'–'],['Asking Price',formatMoney(p.askingPrice, p.currency)],['Sale Price',p.salePrice>0?formatMoney(p.salePrice, p.currency):'–'],['Finished Value',formatMoney(p.finishedValue, p.currency)],['Expected Rent',`${formatMoney(p.expectedRent, p.currency)}/mo`]].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border-light)',gap:'1rem'}}><span style={{color:'var(--text-secondary)',whiteSpace:'nowrap'}}>{l}</span><strong style={{textAlign:'right'}}>{v}</strong></div>
              ))}
              {link&&<div style={{marginTop:'1rem'}}><a href={link} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{display:'block',textAlign:'center',textDecoration:'none'}}>🔗 View Property Listing</a></div>}
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">💰 Monthly Expenses</span></div>
              {[['Management',p.expenseManagement],['Utilities',p.expenseUtilities],['Mortgage',p.expenseMortgage],['Repairs/Maintenance',p.expenseRepairs]].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border-light)'}}><span style={{color:'var(--text-secondary)'}}>{l}</span><strong>{formatMoney(v||0, p.currency)}</strong></div>
              ))}
              <div style={{display:'flex',justifyContent:'space-between',padding:'0.75rem 0',fontWeight:700}}>
                <span>Total Monthly</span><span style={{color:'var(--danger)'}}>{formatMoney(roi.monthlyExp, p.currency)}</span>
              </div>
              {p.mortgageRate>0&&<div style={{background:'#eff6ff',padding:'0.75rem',borderRadius:'0.5rem',marginTop:'0.5rem',fontSize:'0.875rem'}}><strong>Mortgage Rate:</strong> {p.mortgageRate}%</div>}
              {p.notes&&<div style={{marginTop:'1rem',padding:'0.75rem',background:'var(--bg-light)',borderRadius:'0.5rem',fontSize:'0.875rem'}}><strong>Notes:</strong><br/>{p.notes}</div>}
            </div>
          </div>
        )}

        {/* REFURB */}
        {activeTab==='refurb'&&(
          <div className="card">
            <div className="card-header">
              <span className="card-title">🔧 Refurbishment Breakdown</span>
              <button className="btn btn-secondary btn-sm" onClick={()=>openRefurbDirect(p)}>✏️ Edit Refurb</button>
            </div>
            <div style={{background:'#eff6ff',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700,color:'#1e40af'}}>Total Refurb Cost:</span>
              <span style={{fontSize:'1.5rem',fontWeight:700,color:'#1e40af'}}>{formatMoney(p.refurbCosts, p.currency)}</span>
            </div>
            {refurbEntries.length>0?(
              <div>
                {refurbEntries.map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border-light)'}}>
                    <span style={{color:'var(--text-secondary)'}}>{k}</span><strong>{formatMoney(v, p.currency)}</strong>
                  </div>
                ))}
              </div>
            ):<p style={{color:'var(--text-secondary)',textAlign:'center',padding:'2rem'}}>No refurb breakdown — click Edit to add itemised costs</p>}
          </div>
        )}

        {/* REFINANCE */}
        {activeTab==='refinance'&&(
          <div>
            <div style={{background:'#dbeafe',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#1e40af'}}>
              💡 <strong>Refinance Calculator</strong> — Based on finished value of {formatMoney(p.finishedValue, p.currency)} and mortgage rate of {p.mortgageRate||0}%
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:'1rem'}}>
              {roi.scenarios.map(s=>(
                <div key={s.ltv} className="card" style={{borderTop:`4px solid ${s.cashOut>=0?'#22c55e':'#ef4444'}`}}>
                  <div style={{fontWeight:700,fontSize:'1.1rem',marginBottom:'1rem'}}>{s.ltv}% LTV</div>
                  {[
                    ['Mortgage Amount',formatMoney(s.mortAmt, p.currency)],
                    ['Cash Out',s.cashOut>=0?<span style={{color:'#15803d',fontWeight:600}}>{formatMoney(s.cashOut, p.currency)}</span>:<span style={{color:'#dc2626',fontWeight:600}}>{formatMoney(Math.abs(s.cashOut), p.currency)} needed</span>],
                    ['Capital Left In',formatMoney(Math.max(0,s.leftIn), p.currency)],
                    ['ROCE',<span style={{color:s.roce>=20?'#15803d':s.roce>=10?'#d97706':'#dc2626',fontWeight:600}}>{s.roce.toFixed(1)}%</span>],
                    ...(p.mortgageRate>0?[['Interest Only /mo',formatMoney(s.monthlyMort, p.currency)]]:[] )
                  ].map(([l,v])=>(
                    <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'0.375rem 0',borderBottom:'1px solid var(--border-light)',fontSize:'0.875rem'}}>
                      <span style={{color:'var(--text-secondary)'}}>{l}</span><strong>{v}</strong>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PROJECTIONS */}
        {activeTab==='projections'&&(
          <div>
            <div style={{background:'#eff6ff',padding:'1.5rem',borderRadius:'0.5rem',marginBottom:'1.5rem',border:'1px solid #bfdbfe'}}>
              <h4 style={{margin:'0 0 1rem',color:'#1e40af'}}>📈 Growth Rate Assumptions</h4>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.5rem'}}>
                <div className="form-group">
                  <label style={{color:'#1e40af',fontWeight:600}}>Annual Rental Increase (%)</label>
                  <select value={rentalGrowth} onChange={e=>setRentalGrowth(parseFloat(e.target.value))} style={{border:'2px solid #3b82f6'}}>
                    {[1,2,3,4,5].map(v=><option key={v} value={v}>{v}%</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{color:'#1e40af',fontWeight:600}}>Annual Property Value Increase (%)</label>
                  <select value={valueGrowth} onChange={e=>setValueGrowth(parseFloat(e.target.value))} style={{border:'2px solid #3b82f6'}}>
                    {[1,2,3,4,5].map(v=><option key={v} value={v}>{v}%</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'1rem',marginBottom:'2rem'}}>
              {[[proj5,5,'#dcfce7','#15803d','#16a34a'],[proj10,10,'#dbeafe','#1e40af','#3b82f6'],[proj20,20,'#fef3c7','#92400e','#f59e0b']].map(([proj,yrs,bg,tc,bc])=>(
                <div key={yrs} style={{background:bg,padding:'1.5rem',borderRadius:'0.5rem',border:`1px solid ${bc}`}}>
                  <p style={{fontSize:'0.875rem',color:tc,marginBottom:'0.5rem',fontWeight:600}}>{yrs} Year Total ROI</p>
                  <p style={{fontSize:'2rem',fontWeight:'bold',color:tc}}>{proj[yrs-1]?.totalROI.toFixed(1)}%</p>
                  <p style={{fontSize:'0.75rem',color:tc,marginTop:'0.5rem'}}>Total Return: {formatMoney(proj[yrs-1]?.totalReturn||0, p.currency)}</p>
                </div>
              ))}
            </div>

            {/* Projection tables */}
            {[[proj5,'5 Year'],[proj10,'10 Year'],[proj20,'20 Year']].map(([proj,label])=>(
              <div key={label} style={{marginBottom:'2rem'}}>
                <h4 style={{marginBottom:'1rem'}}>{label} Projection</h4>
                <div className="table-container">
                  <table className="table">
                    <thead><tr><th>Year</th><th>Property Value</th><th>Monthly Rent</th><th>Annual Net Income</th><th>Equity</th><th>Cumulative Income</th></tr></thead>
                    <tbody>
                      {proj.map(r=>(
                        <tr key={r.year}>
                          <td><strong>{r.year}</strong></td>
                          <td>{formatMoney(r.propValue, p.currency)}</td>
                          <td>{formatMoney(r.monthlyRent, p.currency)}</td>
                          <td style={{color:'#15803d',fontWeight:600}}>+{formatMoney(r.yearlyNet, p.currency)}</td>
                          <td>{formatMoney(r.equity, p.currency)}</td>
                          <td>{formatMoney(r.cumNet, p.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab==='documents'&&(
          <div>
            <div className="card" style={{marginBottom:'1rem'}}>
              <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span className="card-title">📎 Documents & Photos ({docs.length})</span>
                <div>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" style={{display:'none'}} onChange={e=>handleFileUpload(e,p)}/>
                  <button className="btn btn-primary" onClick={()=>fileInputRef.current?.click()}>+ Upload Files</button>
                </div>
              </div>
              <p style={{fontSize:'0.875rem',color:'var(--text-secondary)',marginBottom:'1rem'}}>Upload photos, PDFs, surveys, or any relevant documents for this property.</p>
              {docs.length===0?(
                <div className="empty-state" style={{padding:'2rem'}}>
                  <span>📎</span><h3>No Documents Yet</h3>
                  <p>Upload photos, surveys, floor plans, or any relevant documents</p>
                  <button className="btn btn-primary" onClick={()=>fileInputRef.current?.click()}>Upload First Document</button>
                </div>
              ):(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'1rem'}}>
                  {docs.map((doc,idx)=>(
                    <div key={idx} style={{border:'1px solid var(--border-light)',borderRadius:'0.5rem',padding:'0.75rem',textAlign:'center'}}>
                      {doc.type?.startsWith('image/')?(
                        <img src={doc.data} alt={doc.name} style={{width:'100%',height:'120px',objectFit:'cover',borderRadius:'0.25rem',marginBottom:'0.5rem'}}/>
                      ):(
                        <div style={{width:'100%',height:'120px',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg-light)',borderRadius:'0.25rem',marginBottom:'0.5rem',fontSize:'2rem'}}>
                          {doc.type?.includes('pdf')?'📄':doc.type?.includes('sheet')||doc.name?.endsWith('.xlsx')?'📊':'📁'}
                        </div>
                      )}
                      <div style={{fontSize:'0.75rem',fontWeight:600,marginBottom:'0.25rem',wordBreak:'break-all'}}>{doc.name}</div>
                      <div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginBottom:'0.5rem'}}>{doc.size?`${(doc.size/1024).toFixed(1)}KB`:''}</div>
                      <div style={{display:'flex',gap:'0.5rem',justifyContent:'center'}}>
                        <a href={doc.data} download={doc.name} className="btn btn-secondary" style={{padding:'0.2rem 0.5rem',fontSize:'0.7rem',textDecoration:'none'}}>⬇️</a>
                        <button className="btn btn-danger" style={{padding:'0.2rem 0.5rem',fontSize:'0.7rem'}} onClick={()=>handleDeleteDoc(p,idx)}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      {/* EDIT/ADD MODAL - accessible from detail view */}
      {showModal&&(
        <div className="modal-overlay" style={{cursor:'default'}} onClick={(e)=>{if(e.target===e.currentTarget)setShowModal(false);}}>
          <div className="modal" style={{maxWidth:'760px'}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">✏️ Edit Prospect</span><button className="modal-close" onClick={()=>setShowModal(false)}>×</button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Status</label><select value={form.status} onChange={e=>set('status',e.target.value)}>{STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
              <div className="form-group"><label>Address *</label><input value={form.address} onChange={e=>set('address',e.target.value)} required/></div>
              <div className="form-group"><label>Currency</label><select value={form.currency} onChange={e=>set('currency',e.target.value)}><option value="GBP">GBP — British Pound (£)</option><option value="EUR">EUR — Euro (€)</option></select></div>
              <div className="form-grid">
                <div className="form-group"><label>Asking Price ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.askingPrice} onChange={e=>set('askingPrice',e.target.value)} required/></div>
                <div className="form-group"><label>Sale Price ({symbolFor(form.currency)})</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.salePrice} onChange={e=>set('salePrice',e.target.value)}/></div>
                <div className="form-group"><label>Bedrooms</label><input type="number" onWheel={e=>e.target.blur()} min="0" value={form.bedrooms} onChange={e=>set('bedrooms',e.target.value)}/></div>
                <div className="form-group"><label>Finished Value ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.finishedValue} onChange={e=>set('finishedValue',e.target.value)} required/></div>
                <div className="form-group"><label>Expected Monthly Rent ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expectedRent} onChange={e=>set('expectedRent',e.target.value)} required/></div>
                <div className="form-group"><label>Refurbishment ({symbolFor(form.currency)})</label><div style={{display:'flex',gap:'0.5rem'}}><input type="number" onWheel={e=>e.target.blur()} value={refurbTotal} readOnly style={{background:'var(--bg-light)'}}/><button type="button" className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={()=>setShowRefurb(true)}>🧮</button></div></div>
                <div className="form-group"><label>Purchase Costs ({symbolFor(form.currency)})</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.purchaseCosts} onChange={e=>set('purchaseCosts',e.target.value)}/></div>
                <div className="form-group"><label>Management ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseManagement} onChange={e=>set('expenseManagement',e.target.value)}/></div>
                <div className="form-group"><label>Utilities ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseUtilities} onChange={e=>set('expenseUtilities',e.target.value)}/></div>
                <div className="form-group"><label>Mortgage Payment ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseMortgage} onChange={e=>set('expenseMortgage',e.target.value)}/></div>
                <div className="form-group"><label>Repairs ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseRepairs} onChange={e=>set('expenseRepairs',e.target.value)}/></div>
                <div className="form-group"><label>Mortgage Rate (%)</label><input type="number" onWheel={e=>e.target.blur()} step="0.01" value={form.mortgageRate} onChange={e=>set('mortgageRate',e.target.value)}/></div>
              </div>
              <div style={{background:'#eff6ff',padding:'0.75rem',borderRadius:'0.5rem',marginBottom:'1rem'}}><strong style={{color:'#1e40af'}}>Total Monthly: {formatMoney(totalMonthlyExp, form.currency)}</strong></div>
              <div className="form-group"><label>Property Link</label><input type="url" value={form.propertyLink} onChange={e=>set('propertyLink',e.target.value)} placeholder="https://www.rightmove.co.uk/..."/></div>
              <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows="3"/></div>
              <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Saving...':'💾 Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REFURB CALCULATOR */}
      {showRefurb&&(
        <div className="modal-overlay" onClick={()=>setShowRefurb(false)}>
          <div className="modal" style={{maxWidth:'680px'}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">🧮 Refurbishment Cost Calculator</span><button className="modal-close" onClick={()=>setShowRefurb(false)}>×</button></div>
            <div style={{background:'#eff6ff',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1.5rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700,color:'#1e40af'}}>Total Estimated Cost:</span>
              <span style={{fontSize:'1.5rem',fontWeight:700,color:'#1e40af'}}>{formatMoney(refurbTotal, form.currency)}</span>
            </div>
            <div style={{maxHeight:'400px',overflowY:'auto',paddingRight:'0.5rem'}}>
              <div className="form-grid">
                {REFURB_CATS.map(cat=>(
                  <div key={cat} className="form-group">
                    <label>{cat}</label>
                    <input type="number" onWheel={e=>e.target.blur()} step="1" min="0" value={refurbBreakdown[cat]||''} onChange={e=>setRefurbBreakdown(p=>({...p,[cat]:e.target.value}))} placeholder="0"/>
                  </div>
                ))}
              </div>
              <div style={{borderTop:'2px solid var(--border-light)',marginTop:'1.5rem',paddingTop:'1.5rem'}}>
                <div className="form-group"><label>💰 Contingency (recommended 10-15%)</label><input type="number" onWheel={e=>e.target.blur()} step="1" min="0" value={refurbBreakdown['Contingency']||''} onChange={e=>setRefurbBreakdown(p=>({...p,Contingency:e.target.value}))} placeholder="0"/></div>
              </div>
              <div style={{borderTop:'2px solid var(--border-light)',marginTop:'1.5rem',paddingTop:'1.5rem'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
                  <h4>Other Items</h4>
                  <button type="button" className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={()=>setCustomItems(p=>[...p,{name:'',cost:''}])}>+ Add Item</button>
                </div>
                {customItems.map((item,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 120px 36px',gap:'0.5rem',marginBottom:'0.5rem',alignItems:'center'}}>
                    <input value={item.name} onChange={e=>setCustomItems(p=>p.map((x,j)=>j===i?{...x,name:e.target.value}:x))} placeholder="Item name..."/>
                    <input type="number" onWheel={e=>e.target.blur()} step="1" value={item.cost} onChange={e=>setCustomItems(p=>p.map((x,j)=>j===i?{...x,cost:e.target.value}:x))} placeholder={symbolFor(form.currency)}/>
                    <button type="button" className="btn btn-danger" style={{padding:'0.4rem'}} onClick={()=>setCustomItems(p=>p.filter((_,j)=>j!==i))}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end',marginTop:'1.5rem'}}>
              <button type="button" className="btn btn-secondary" onClick={()=>setShowRefurb(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={async()=>{
                setShowRefurb(false);
                // If launched directly (not from inside the property edit modal), auto-save the refurb changes
                if (!showModal && editProspect) {
                  try {
                    const existingDocs = normalise(editProspect).documents||[];
                    const n = normalise(editProspect);
                    const breakdown = buildPayload(existingDocs, n.bedrooms);
                    const data = {
                      status: n.status, address: n.address,
                      askingPrice: n.askingPrice, salePrice: n.salePrice,
                      bedrooms: n.bedrooms !== undefined && n.bedrooms !== null ? parseInt(n.bedrooms, 10) : 0,
                      refurbCosts: Math.round(refurbTotal),
                      purchaseCosts: n.purchaseCosts,
                      finishedValue: n.finishedValue,
                      expectedRent: n.expectedRent,
                      monthlyExpenses: n.expenseManagement+n.expenseUtilities+n.expenseMortgage+n.expenseRepairs,
                      mortgageRate: n.mortgageRate,
                      notes: n.notes, propertyLink: n.propertyLink, currency: n.currency,
                      refurbBreakdown: breakdown,
                      expenseManagement: n.expenseManagement, expenseUtilities: n.expenseUtilities,
                      expenseMortgage: n.expenseMortgage, expenseRepairs: n.expenseRepairs,
                      archived: n.archived||false
                    };
                    const r = await prospectsAPI.update(editProspect.id, data);
                    const updated = normalise(r);
                    setProspects(prev => prev.map(px => px.id === editProspect.id ? updated : px));
                    if (selected?.id === editProspect.id) setSelected(updated);
                  } catch(err) { alert(err.error||'Failed to save refurb'); }
                }
              }}>✅ Apply ({formatMoney(refurbTotal, form.currency)})</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  }

  // ─── COMPARISON MODAL ─────────────────────────────────────────────────────────
  if (showCompare && compareIds.length===2) {
    const p1 = nProspects.find(x=>x.id===compareIds[0]);
    const p2 = nProspects.find(x=>x.id===compareIds[1]);
    if (!p1||!p2) { setShowCompare(false); return null; }
    const roi1=calcROI(p1), roi2=calcROI(p2);
    const s1=calcScore(roi1,roi1.cashFlow), s2=calcScore(roi2,roi2.cashFlow);
    const winner = s1>s2?p1:s2>s1?p2:null;
    // BUG-005e: each prospect's figures use its own currency symbol
    const fmtFor = (cur) => v => formatMoney(v||0, cur);
    const pct = v => `${parseFloat(v||0).toFixed(2)}%`;
    const col = (v,good,ok) => v>=good?'#15803d':v>=ok?'#d97706':'#dc2626';

    const PropCard = ({prop,roi,score,isWinner}) => { const fmt = fmtFor(prop.currency); return (
      <div>
        <h3 style={{margin:'0 0 1rem',color:'#1e293b',borderBottom:`2px solid ${isWinner?'#16a34a':'#3b82f6'}`,paddingBottom:'0.5rem'}}>{prop.address}</h3>
        <div style={{background:'#f8fafc',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
          <h4 style={{margin:'0 0 0.75rem',color:'var(--text-secondary)',fontSize:'0.875rem'}}>PROPERTY DETAILS</h4>
          {[['Asking Price',fmt(prop.askingPrice)],['Bedrooms',prop.bedrooms||0],['Expected Rent',`${fmt(prop.expectedRent)}/mo`],['Refurb Costs',fmt(prop.refurbCosts)],['Total Investment',fmt(roi.totalInv)]].map(([l,v])=>(
            <div key={l} style={{display:'flex',justifyContent:'space-between',marginBottom:'0.375rem',fontSize:'0.875rem'}}><span style={{color:'var(--text-secondary)'}}>{l}:</span><strong>{v}</strong></div>
          ))}
          <div style={{display:'flex',justifyContent:'space-between',marginTop:'0.5rem',padding:'0.5rem',background:'#fff3cd',borderRadius:'0.25rem',fontSize:'0.875rem'}}>
            <span style={{color:'#856404',fontWeight:600}}>Money Left in (75%):</span>
            <strong style={{color:'#856404'}}>{fmt(roi.scenarios[0]?.leftIn||0)}</strong>
          </div>
        </div>
        <div style={{background:isWinner?'#dcfce7':'#f8fafc',padding:'1rem',borderRadius:'0.5rem',border:`2px solid ${isWinner?'#16a34a':'#e2e8f0'}`}}>
          <h4 style={{margin:'0 0 0.75rem',color:'var(--text-secondary)',fontSize:'0.875rem'}}>KEY METRICS</h4>
          {[
            ['Monthly Cash Flow',fmt(roi.cashFlow),col(roi.cashFlow,300,100)],
            ['Annual Profit',fmt(roi.annNet),col(roi.annNet,3600,1200)],
            ['ROCE (75% LTV)',pct(roi.scenarios[0]?.roce),col(roi.scenarios[0]?.roce||0,15,10)],
            ['Gross Yield',pct(roi.grossYield),col(roi.grossYield,6,4)],
            ['Net Yield',pct(roi.netYield),col(roi.netYield,4,2)],
            ['ROI',pct(roi.roi),col(roi.roi,15,10)]
          ].map(([l,v,c])=>(
            <div key={l} style={{display:'flex',justifyContent:'space-between',marginBottom:'0.375rem',fontSize:'0.875rem'}}>
              <span style={{color:'var(--text-secondary)'}}>{l}:</span><strong style={{color:c}}>{v}</strong>
            </div>
          ))}
          <div style={{display:'flex',justifyContent:'space-between',paddingTop:'0.75rem',borderTop:`2px solid ${isWinner?'#16a34a':'#cbd5e1'}`,marginTop:'0.5rem'}}>
            <span style={{fontWeight:600}}>Overall Score:</span>
            <strong style={{fontSize:'1.5rem',color:isWinner?'#15803d':'#1e293b'}}>{score}/100</strong>
          </div>
        </div>
      </div>
    ); };

    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
          <button className="btn btn-secondary" onClick={()=>setShowCompare(false)}>← Back to Prospects</button>
          <h2 style={{fontSize:'1.25rem',fontWeight:700}}>⚖️ Property Comparison</h2>
          <div/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.5rem'}}>
          <PropCard prop={p1} roi={roi1} score={s1} isWinner={s1>s2}/>
          <PropCard prop={p2} roi={roi2} score={s2} isWinner={s2>s1}/>
        </div>
        {winner?(
          <div style={{background:'#dcfce7',border:'2px solid #16a34a',borderRadius:'0.5rem',padding:'1.5rem',marginTop:'1.5rem',textAlign:'center'}}>
            <p style={{fontSize:'1.5rem',fontWeight:'bold',color:'#15803d',margin:'0 0 0.5rem'}}>🏆 {winner.address} is the Better Investment!</p>
            <p style={{color:'#15803d',margin:'0 0 0.75rem'}}>Score: {Math.max(s1,s2)} vs {Math.min(s1,s2)} ({Math.abs(s1-s2)} points ahead)</p>
            <p style={{fontSize:'0.75rem',color:'#15803d',fontStyle:'italic',margin:0}}>Scoring: Monthly Profit (30%), Money Left in Deal (25%), ROCE (20%), Yield (15%), ROI (10%)</p>
          </div>
        ):(
          <div style={{background:'#eff6ff',border:'2px solid #3b82f6',borderRadius:'0.5rem',padding:'1.5rem',marginTop:'1.5rem',textAlign:'center'}}>
            <p style={{fontSize:'1.25rem',fontWeight:'bold',color:'#1e40af',margin:0}}>🤝 Both properties scored equally! ({s1}/100)</p>
          </div>
        )}
      </div>
    );
  }

  // ─── ARCHIVE VIEW ─────────────────────────────────────────────────────────────
  if (showArchive) {
    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
          <button className="btn btn-secondary" onClick={()=>setShowArchive(false)}>← Back to Prospects</button>
          <h2 style={{fontSize:'1.25rem',fontWeight:700}}>📦 Archived Prospects ({archived.length})</h2>
          <div/>
        </div>
        {archived.length===0?(
          <div className="card"><div className="empty-state"><span>📦</span><h3>No Archived Prospects</h3></div></div>
        ):(
          <div className="property-grid">
            {archived.map(p=>(
              <div key={p.id} className="property-card" style={{opacity:0.8}}>
                <div className="property-card-header">
                  <div><div style={{fontWeight:700}}>{p.address}</div><div style={{fontSize:'0.75rem',opacity:0.9}}>{formatMoney(p.askingPrice, p.currency)} • {p.status}</div></div>
                </div>
                <div className="property-card-body">
                  <div style={{display:'flex',gap:'0.5rem'}}>
                    <button className="btn btn-secondary" style={{flex:1,fontSize:'0.75rem'}} onClick={()=>{setSelected(p);setShowArchive(false);}}>View</button>
                    <button className="btn btn-success" style={{flex:1,fontSize:'0.75rem'}} onClick={()=>handleArchive(p)}>📤 Restore</button>
                    <button className="btn btn-danger" style={{fontSize:'0.75rem'}} onClick={()=>handleDelete(p.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Prospective Properties</h1>
        </div>
        <div style={{display:'flex',gap:'0.75rem'}}>
          {archived.length>0&&<button className="btn btn-secondary" onClick={()=>setShowArchive(true)}>📦 Archive ({archived.length})</button>}
          {compareIds.length>0&&(
            <button className={`btn ${compareIds.length===2?'btn-success':'btn-secondary'}`} onClick={()=>compareIds.length===2?setShowCompare(true):alert('Select 2 properties to compare')} style={{fontWeight:600}}>
              ⚖️ Compare ({compareIds.length}/2)
            </button>
          )}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Prospect</button>
        </div>
      </div>

      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Active Prospects</div><div className="stat-value">{active.length}</div></div>
        <div className="stat-card"><div className="stat-label">Offer/Legals</div><div className="stat-value positive">{active.filter(p=>['offer-made','offer-accepted','legals'].includes(p.status)).length}</div></div>
        <div className="stat-card"><div className="stat-label">Purchased</div><div className="stat-value">{active.filter(p=>p.status==='purchased').length}</div></div>
        <div className="stat-card"><div className="stat-label">Archived</div><div className="stat-value" style={{color:'var(--text-secondary)'}}>{archived.length}</div></div>
      </div>

      {compareIds.length>0&&(
        <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1rem',display:'flex',alignItems:'center',gap:'1rem',fontSize:'0.875rem',color:'#1e40af'}}>
          ⚖️ <strong>Comparison Mode:</strong> Select 2 properties using the checkboxes. {compareIds.length===2?'Click Compare to view side-by-side analysis.':'Select one more property.'}
          <button className="btn btn-secondary" style={{marginLeft:'auto',fontSize:'0.75rem',padding:'0.25rem 0.75rem'}} onClick={()=>setCompareIds([])}>Clear</button>
        </div>
      )}

      {active.length===0?(
        <div className="card"><div className="empty-state"><span>🏘️</span><h3>No Prospects Yet</h3><p>Track potential property investments with ROI analysis</p><button className="btn btn-primary" onClick={openAdd}>Add First Prospect</button></div></div>
      ):(
        <div className="property-grid">
          {active.map(p=>{
            const roi=calcROI(p);
            const status=STATUSES.find(s=>s.value===p.status)||STATUSES[0];
            const isSelected=compareIds.includes(p.id);
            return(
              <div key={p.id} className="property-card" style={{outline:isSelected?'3px solid #3b82f6':'none'}} onClick={()=>{setSelected(p);setActiveTab('analysis');}}>
                <div className="property-card-header">
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:'1rem'}}>{p.address}</div>
                      <div style={{fontSize:'0.75rem',opacity:0.9}}>{p.bedrooms>0?`${p.bedrooms} bed • `:''}{formatMoney(p.askingPrice, p.currency)}</div>
                    </div>
                    <span style={{background:'rgba(255,255,255,0.2)',padding:'0.2rem 0.5rem',borderRadius:'1rem',fontSize:'0.7rem',fontWeight:600}}>{status.label}</span>
                  </div>
                </div>
                <div className="property-card-body">
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',fontSize:'0.8rem',marginBottom:'0.75rem'}}>
                    <div><span style={{color:'var(--text-secondary)'}}>Gross Yield: </span><strong style={{color:roi.grossYield>=8?'#15803d':roi.grossYield>=6?'#d97706':'#dc2626'}}>{roi.grossYield.toFixed(1)}%</strong></div>
                    <div><span style={{color:'var(--text-secondary)'}}>Cash Flow: </span><strong style={{color:roi.cashFlow>=0?'#15803d':'#dc2626'}}>{formatMoney(roi.cashFlow, p.currency)}/mo</strong></div>
                    <div><span style={{color:'var(--text-secondary)'}}>ROI: </span><strong>{roi.roi.toFixed(1)}%</strong></div>
                    <div><span style={{color:'var(--text-secondary)'}}>Capital Gain: </span><strong style={{color:roi.capitalGain>=0?'#15803d':'#dc2626'}}>{formatMoney(roi.capitalGain, p.currency)}</strong></div>
                  </div>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}} onClick={e=>e.stopPropagation()}>
                    <label style={{display:'flex',alignItems:'center',gap:'0.25rem',fontSize:'0.75rem',cursor:'pointer',padding:'0.2rem 0.5rem',border:'1px solid var(--border-light)',borderRadius:'0.25rem',background:isSelected?'#eff6ff':'white'}}>
                      <input type="checkbox" checked={isSelected} onChange={()=>toggleCompare(p.id)} style={{width:'14px',height:'14px'}}/> Compare
                    </label>
                    <button className="btn btn-secondary" style={{flex:1,padding:'0.3rem',fontSize:'0.75rem'}} onClick={()=>{setSelected(p);setActiveTab('analysis');}}>📊 Analyse</button>
                    <button className="btn btn-secondary" style={{padding:'0.3rem 0.5rem',fontSize:'0.75rem'}} onClick={()=>openEdit(p)}>✏️</button>
                    <button className="btn btn-secondary" style={{padding:'0.3rem 0.5rem',fontSize:'0.75rem',background:'#f59e0b',color:'white',border:'none'}} onClick={()=>handleArchive(p)}>📦</button>
                    <button className="btn btn-danger" style={{padding:'0.3rem 0.5rem',fontSize:'0.75rem'}} onClick={()=>handleDelete(p.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal&&(
        <div className="modal-overlay" style={{cursor:'default'}} onClick={(e)=>{if(e.target===e.currentTarget)setShowModal(false);}}>
          <div className="modal" style={{maxWidth:'760px'}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editProspect?'✏️ Edit Prospect':'🏘️ Add Prospective Property'}</span>
              <button className="modal-close" onClick={()=>setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Status *</label>
                <select value={form.status} onChange={e=>set('status',e.target.value)}>
                  {STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <h4 style={{margin:'1rem 0 0.75rem',color:'var(--brand-green)'}}>Property Details</h4>
              <div className="form-group"><label>Address *</label><input value={form.address} onChange={e=>set('address',e.target.value)} required/></div>
              <div className="form-group"><label>Currency</label><select value={form.currency} onChange={e=>set('currency',e.target.value)}><option value="GBP">GBP — British Pound (£)</option><option value="EUR">EUR — Euro (€)</option></select></div>
              <div className="form-grid">
                <div className="form-group"><label>Asking Price ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.askingPrice} onChange={e=>set('askingPrice',e.target.value)} required/></div>
                <div className="form-group"><label>Sale Price ({symbolFor(form.currency)})</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.salePrice} onChange={e=>set('salePrice',e.target.value)} placeholder="If offer accepted"/></div>
                <div className="form-group"><label>Bedrooms</label><input type="number" onWheel={e=>e.target.blur()} min="0" value={form.bedrooms} onChange={e=>set('bedrooms',e.target.value)}/></div>
              </div>
              <h4 style={{margin:'1rem 0 0.75rem',color:'var(--brand-green)'}}>Investment Costs</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Refurbishment ({symbolFor(form.currency)})</label>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                    <input type="number" onWheel={e=>e.target.blur()} value={refurbTotal} readOnly style={{background:'var(--bg-light)'}}/>
                    <button type="button" className="btn btn-secondary" style={{whiteSpace:'nowrap',fontSize:'0.8rem'}} onClick={()=>setShowRefurb(true)}>🧮 Calculate</button>
                  </div>
                </div>
                <div className="form-group"><label>Purchase Costs ({symbolFor(form.currency)})</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.purchaseCosts} onChange={e=>set('purchaseCosts',e.target.value)}/></div>
              </div>
              <h4 style={{margin:'1rem 0 0.75rem',color:'var(--brand-green)'}}>Expected Performance</h4>
              <div className="form-grid">
                <div className="form-group"><label>Finished Value ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.finishedValue} onChange={e=>set('finishedValue',e.target.value)} required/></div>
                <div className="form-group"><label>Expected Monthly Rent ({symbolFor(form.currency)}) *</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expectedRent} onChange={e=>set('expectedRent',e.target.value)} required/></div>
              </div>
              <h4 style={{margin:'1rem 0 0.75rem',color:'var(--brand-green)'}}>Monthly Running Costs</h4>
              <div className="form-grid">
                <div className="form-group"><label>Management Fees ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseManagement} onChange={e=>set('expenseManagement',e.target.value)}/></div>
                <div className="form-group"><label>Utilities ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseUtilities} onChange={e=>set('expenseUtilities',e.target.value)}/></div>
                <div className="form-group"><label>Mortgage Payment ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseMortgage} onChange={e=>set('expenseMortgage',e.target.value)}/></div>
                <div className="form-group"><label>Repairs/Maintenance ({symbolFor(form.currency)}/mo)</label><input type="number" onWheel={e=>e.target.blur()} step="1" value={form.expenseRepairs} onChange={e=>set('expenseRepairs',e.target.value)}/></div>
              </div>
              <div style={{background:'#eff6ff',padding:'0.75rem 1rem',borderRadius:'0.5rem',marginBottom:'1rem',borderLeft:'4px solid #3b82f6'}}>
                <strong style={{color:'#1e40af'}}>Total Monthly Expenses: {formatMoney(totalMonthlyExp, form.currency)}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Mortgage Interest Rate (%)</label><input type="number" onWheel={e=>e.target.blur()} step="0.01" value={form.mortgageRate} onChange={e=>set('mortgageRate',e.target.value)} placeholder="e.g. 5.5"/></div>
                <div className="form-group"><label>Property Link</label><input type="url" value={form.propertyLink} onChange={e=>set('propertyLink',e.target.value)} placeholder="https://www.rightmove.co.uk/..."/></div>
              </div>
              <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows="3"/></div>
              <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Saving...':editProspect?'💾 Save Changes':'✅ Add Prospect'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REFURB CALCULATOR — rendered here so it works when opened from the Add/Edit modal in list view */}
      {showRefurb&&(
        <div className="modal-overlay" onClick={()=>setShowRefurb(false)}>
          <div className="modal" style={{maxWidth:'680px'}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">🧮 Refurbishment Cost Calculator</span><button className="modal-close" onClick={()=>setShowRefurb(false)}>×</button></div>
            <div style={{background:'#eff6ff',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1.5rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700,color:'#1e40af'}}>Total Estimated Cost:</span>
              <span style={{fontSize:'1.5rem',fontWeight:700,color:'#1e40af'}}>{formatMoney(refurbTotal, form.currency)}</span>
            </div>
            <div style={{maxHeight:'400px',overflowY:'auto',paddingRight:'0.5rem'}}>
              <div className="form-grid">
                {REFURB_CATS.map(cat=>(
                  <div key={cat} className="form-group">
                    <label>{cat}</label>
                    <input type="number" onWheel={e=>e.target.blur()} step="1" min="0" value={refurbBreakdown[cat]||''} onChange={e=>setRefurbBreakdown(p=>({...p,[cat]:e.target.value}))} placeholder="0"/>
                  </div>
                ))}
              </div>
              <div style={{borderTop:'2px solid var(--border-light)',marginTop:'1.5rem',paddingTop:'1.5rem'}}>
                <div className="form-group"><label>💰 Contingency (recommended 10-15%)</label><input type="number" onWheel={e=>e.target.blur()} step="1" min="0" value={refurbBreakdown['Contingency']||''} onChange={e=>setRefurbBreakdown(p=>({...p,Contingency:e.target.value}))} placeholder="0"/></div>
              </div>
              <div style={{borderTop:'2px solid var(--border-light)',marginTop:'1.5rem',paddingTop:'1.5rem'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
                  <h4>Other Items</h4>
                  <button type="button" className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={()=>setCustomItems(p=>[...p,{name:'',cost:''}])}>+ Add Item</button>
                </div>
                {customItems.map((item,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 120px 36px',gap:'0.5rem',marginBottom:'0.5rem',alignItems:'center'}}>
                    <input value={item.name} onChange={e=>setCustomItems(p=>p.map((x,j)=>j===i?{...x,name:e.target.value}:x))} placeholder="Item name..."/>
                    <input type="number" onWheel={e=>e.target.blur()} step="1" value={item.cost} onChange={e=>setCustomItems(p=>p.map((x,j)=>j===i?{...x,cost:e.target.value}:x))} placeholder={symbolFor(form.currency)}/>
                    <button type="button" className="btn btn-danger" style={{padding:'0.4rem'}} onClick={()=>setCustomItems(p=>p.filter((_,j)=>j!==i))}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end',marginTop:'1.5rem'}}>
              <button type="button" className="btn btn-secondary" onClick={()=>setShowRefurb(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={async()=>{
                setShowRefurb(false);
                // Auto-save only when launched directly from detail view (not inside the Add/Edit modal)
                if (!showModal && editProspect) {
                  try {
                    const n = normalise(editProspect);
                    const existingDocs = n.documents||[];
                    // Pass n.bedrooms as explicit override — never relies on potentially stale form.bedrooms
                    const breakdown = buildPayload(existingDocs, n.bedrooms);
                    const data = {
                      status: n.status, address: n.address,
                      askingPrice: n.askingPrice, salePrice: n.salePrice,
                      bedrooms: n.bedrooms !== undefined && n.bedrooms !== null ? parseInt(n.bedrooms, 10) : 0,
                      refurbCosts: Math.round(refurbTotal),
                      purchaseCosts: n.purchaseCosts,
                      finishedValue: n.finishedValue,
                      expectedRent: n.expectedRent,
                      monthlyExpenses: n.expenseManagement+n.expenseUtilities+n.expenseMortgage+n.expenseRepairs,
                      mortgageRate: n.mortgageRate,
                      notes: n.notes, propertyLink: n.propertyLink, currency: n.currency,
                      refurbBreakdown: breakdown,
                      expenseManagement: n.expenseManagement, expenseUtilities: n.expenseUtilities,
                      expenseMortgage: n.expenseMortgage, expenseRepairs: n.expenseRepairs,
                      archived: n.archived||false
                    };
                    const r = await prospectsAPI.update(editProspect.id, data);
                    const updated = normalise(r);
                    setProspects(prev => prev.map(px => px.id === editProspect.id ? updated : px));
                    if (selected?.id === editProspect.id) setSelected(updated);
                  } catch(err) { alert(err.error||'Failed to save refurb'); }
                }
              }}>✅ Apply ({formatMoney(refurbTotal, form.currency)})</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
